import { append, equals, includes, reject, uniq } from "ramda";
import { useCallback, useReducer } from "react";

const actionTypes = {
  reset: "reset",
  update_page: "update_page",
  update_query: "update_query",
  update_facets: "update_facets",
  update_orderBy: "update_orderBy",
  update_other: "update_other",
  update_pubYearGrps: "update_pubYearGrps",
  update_limit: "update_limit",
  update_source: "update_source",
  update_types: "update_types",
  update_mediaTypes: "update_mediaTypes",
  update_statuses: "update_statuses",
};

const useSearchQuery = (initialState = {}) => {
  const searchQueryReducer = (state, action) => {
    switch (action.type) {
      case actionTypes.reset:
        return initialState;
      case actionTypes.update_page:
        return { ...state, page: action.page };
      case actionTypes.update_query:
        return {
          ...state,
          appliedQuery: action.query,
          page: 0,
        };
      case actionTypes.update_facets:
        return {
          ...state,
          appliedFacets: action.facets,
          page: 0,
        };
      case actionTypes.update_orderBy:
        return {
          ...state,
          orderBy: action.field,
          orderDirection: action.direction,
          page: initialState.page,
        };
      case actionTypes.update_other:
        return {
          ...state,
          otherParams: action.other,
          page: 0,
        };
      case actionTypes.update_pubYearGrps:
        return {
          ...state,
          appliedPublicationYearGroups: action.pubYearGroups,
          page: 0,
        };
      case actionTypes.update_limit:
        return {
          ...state,
          limit: action.limit,
          page: 0,
        };
      case actionTypes.update_source:
        return {
          ...state,
          source: action.source,
          page: 0,
        };
      case actionTypes.update_types:
        return {
          ...state,
          types: action.types,
          page: 0,
        };
      case actionTypes.update_mediaTypes:
        return {
          ...state,
          mediaTypes: action.mediaTypes,
          page: 0,
        };
      case actionTypes.update_statuses:
        return {
          ...state,
          statuses: action.statuses,
          page: 0,
        };
      default:
        return state;
    }
  };

  const [state, dispatch] = useReducer(searchQueryReducer, initialState);

  const handleUpdateSort = useCallback(({ property, order }) => {
    dispatch({
      type: actionTypes.update_orderBy,
      field: property,
      direction: order,
    });
  }, []);

  const handleUpdateQuery = useCallback(query => {
    dispatch({ type: actionTypes.update_query, query: query });
  }, []);

  const handleUpdatePage = useCallback(page => {
    dispatch({ type: actionTypes.update_page, page: page });
  }, []);

  const handleUpdateFacets = useCallback(facets => {
    dispatch({ type: actionTypes.update_facets, facets: facets });
  }, []);

  const handleUpdateOtherParams = useCallback(other => {
    dispatch({ type: actionTypes.update_other, other: other });
  }, []);

  const handleUpdateLimit = useCallback(limit => {
    dispatch({ type: actionTypes.update_limit, limit });
  }, []);

  const handleUpdateSource = useCallback(source => {
    dispatch({ type: actionTypes.update_source, source });
  }, []);

  const handleUpdateTypes = useCallback(types => {
    dispatch({ type: actionTypes.update_types, types });
  }, []);

  const handleUpdateTypeSelect = useCallback(
    term => {
      const types = includes(term.queryParam, state.types)
        ? reject(type => equals(type, term.queryParam), state.types)
        : uniq(append(term.queryParam, state.types));

      dispatch({ type: actionTypes.update_types, types });
    },
    [state.types],
  );

  const handleUpdateMediaTypes = useCallback(mediaTypes => {
    dispatch({ type: actionTypes.update_mediaTypes, mediaTypes });
  }, []);

  const handleUpdateStatuses = useCallback(statuses => {
    dispatch({ type: actionTypes.update_statuses, statuses });
  }, []);

  const handleUpdateStatusSelect = useCallback(
    term => {
      const statuses = includes(term.queryParam, state.statuses)
        ? reject(status => equals(status, term.queryParam), state.statuses)
        : uniq(append(term.queryParam, state.statuses));

      dispatch({ type: actionTypes.update_statuses, statuses });
    },
    [state.statuses],
  );

  const reset = () => dispatch({ type: actionTypes.reset });

  return {
    state,
    reset,
    handleUpdateSort,
    handleUpdateQuery,
    handleUpdatePage,
    handleUpdateFacets,
    handleUpdateOtherParams,
    handleUpdateLimit,
    handleUpdateSource,
    handleUpdateTypeSelect,
    handleUpdateTypes,
    handleUpdateMediaTypes,
    handleUpdateStatusSelect,
    handleUpdateStatuses,
  };
};

export { actionTypes, useSearchQuery };
